import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

/**
 * Banner cookies (Faza 11). System używa wyłącznie cookies niezbędnych
 * (sesja staffa + język UI), więc nie potrzebujemy zgody opt-in zgodnie
 * z RODO/ePrivacy — wystarczy wyraźna informacja. Banner ma jeden przycisk
 * potwierdzenia (decyzja użytkownika 2026-05-09).
 *
 * Stan zapamiętany w localStorage (`cookie-acknowledged=1`). Klucz świadomie
 * krótki, bo to tylko niewrażliwy bit "user already saw this banner".
 */

const STORAGE_KEY = 'cookie-acknowledged';

export function CookieBanner(): ReactNode {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  // SSR-friendly init: domyślnie ukryty, włączamy w useEffect po mount.
  // Dzięki temu unikamy mignięcia banneru gdy ktoś już go zaakceptował.
  useEffect(() => {
    try {
      const v = window.localStorage.getItem(STORAGE_KEY);
      if (v !== '1') setVisible(true);
    } catch {
      // Tryb prywatny / disabled storage — pokaż banner, ale nie blokuj.
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  const onAccept = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t('cookies.title')}
      className="fixed inset-x-0 bottom-0 z-50 px-3 pb-3 sm:px-6 sm:pb-6"
    >
      <div className="mx-auto flex max-w-3xl flex-col gap-3 rounded-lg bg-zinc-900 px-4 py-4 text-sm text-zinc-100 shadow-lg ring-1 ring-zinc-700 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex-1">
          <p className="font-medium">{t('cookies.title')}</p>
          <p className="mt-1 text-xs text-zinc-300">
            {t('cookies.body')}{' '}
            <Link
              to="/privacy"
              className="underline decoration-dotted underline-offset-2 hover:text-white"
            >
              {t('cookies.learnMore')}
            </Link>
          </p>
        </div>
        <button
          type="button"
          onClick={onAccept}
          className="self-start rounded-md bg-white px-4 py-2 text-sm font-medium text-zinc-900 transition hover:bg-zinc-200 sm:self-auto"
        >
          {t('cookies.accept')}
        </button>
      </div>
    </div>
  );
}
